import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Expand, FoldVertical, Maximize, Minimize, Minus, Plus, Scan } from 'lucide-react'
import { branchPath, layoutMindmap, overviewFolds, parseMindmap } from './mindmapLayout'

function MindmapCanvas({ tree }) {
    const containerRef = useRef(null)
    const svgRef = useRef(null)
    const dragRef = useRef(null)
    const anchorRef = useRef(null)
    const [folded, setFolded] = useState(() => overviewFolds(tree))
    const [viewport, setViewport] = useState({ width: 0, height: 0 })
    const [view, setView] = useState({ x: 0, y: 0, scale: 1 })
    const [fullscreen, setFullscreen] = useState(false)
    const layout = useMemo(() => layoutMindmap(tree, folded), [tree, folded])
    const layoutRef = useRef(layout)
    layoutRef.current = layout

    const fit = useCallback(() => {
        const { width, height } = svgRef.current.getBoundingClientRect()
        if (width <= 48 || height <= 64) return
        const { left, right, top, bottom } = layoutRef.current.bounds
        const scale = Math.min(1, (width - 48) / (right - left), (height - 64) / (bottom - top))
        setView({ x: width / 2 - ((left + right) / 2) * scale, y: height / 2 - ((top + bottom) / 2) * scale, scale })
    }, [])

    useLayoutEffect(() => {
        const observer = new ResizeObserver(([entry]) => {
            const { width, height } = entry.contentRect
            setViewport({ width, height })
            fit()
        })
        observer.observe(svgRef.current)
        return () => observer.disconnect()
    }, [fit])

    useLayoutEffect(() => {
        const anchor = anchorRef.current
        if (anchor) {
            // 展开时保持被点击节点的位置与字号，不把整张图缩成无法阅读的小字。
            const next = layout.nodes.find((node) => node.id === anchor.id)
            setView((current) => ({ ...current, x: current.x + (anchor.x - next.x) * current.scale, y: current.y + (anchor.y - next.y) * current.scale }))
            anchorRef.current = null
        } else {
            fit()
        }
    }, [layout, fit])

    const zoom = useCallback((factor, point) => {
        const bounds = svgRef.current.getBoundingClientRect()
        const x = point ? point.x - bounds.left : bounds.width / 2
        const y = point ? point.y - bounds.top : bounds.height / 2
        setView((current) => {
            const scale = Math.min(2.5, Math.max(0.01, current.scale * factor))
            const ratio = scale / current.scale
            return { x: x - (x - current.x) * ratio, y: y - (y - current.y) * ratio, scale }
        })
    }, [])

    useEffect(() => {
        const svg = svgRef.current
        const wheel = (event) => {
            event.preventDefault()
            zoom(Math.exp(-event.deltaY * 0.002), { x: event.clientX, y: event.clientY })
        }
        svg.addEventListener('wheel', wheel, { passive: false })
        return () => svg.removeEventListener('wheel', wheel)
    }, [zoom])

    useEffect(() => {
        const change = () => setFullscreen(document.fullscreenElement === containerRef.current)
        document.addEventListener('fullscreenchange', change)
        return () => document.removeEventListener('fullscreenchange', change)
    }, [])

    const toggle = (node) => {
        anchorRef.current = node
        setFolded((current) => {
            const next = new Set(current)
            if (next.has(node.id)) next.delete(node.id)
            else next.add(node.id)
            return next
        })
    }
    const pointerDown = (event) => {
        if (event.button !== 0 || event.target.closest('[data-map-toggle]')) return
        event.currentTarget.setPointerCapture(event.pointerId)
        dragRef.current = { x: event.clientX, y: event.clientY, view }
    }
    const pointerMove = (event) => {
        const drag = dragRef.current
        if (!drag || !event.currentTarget.hasPointerCapture(event.pointerId)) return
        setView({ ...drag.view, x: drag.view.x + event.clientX - drag.x, y: drag.view.y + event.clientY - drag.y })
    }
    const pointerEnd = (event) => {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId)
        dragRef.current = null
    }
    const toggleFullscreen = async () => {
        try {
            if (document.fullscreenElement) await document.exitFullscreen()
            else await containerRef.current.requestFullscreen()
        } catch (error) {
            console.warn('浏览器未允许导图全屏', error)
        }
    }

    return (
        <div className="inventory-mindmap" ref={containerRef}>
            <div className="inventory-map-toolbar" aria-label="思维导图工具">
                <div className="inventory-map-presets">
                    <button onClick={() => setFolded(overviewFolds(tree))} title="恢复默认展开层级并适应画布">
                        <FoldVertical size={15} /> 总览
                    </button>
                    <button onClick={() => setFolded(new Set())}>
                        <Expand size={15} /> 展开全部
                    </button>
                </div>
                <div className="inventory-map-tools">
                    <button aria-label="缩小导图" title="缩小" onClick={() => zoom(0.8)}>
                        <Minus size={16} />
                    </button>
                    <button className="inventory-map-scale" aria-label="还原为百分之百" title="原始大小" onClick={() => zoom(1 / view.scale)}>
                        {Math.round(view.scale * 100)}%
                    </button>
                    <button aria-label="放大导图" title="放大" onClick={() => zoom(1.25)}>
                        <Plus size={16} />
                    </button>
                    <span className="inventory-map-tool-divider" />
                    <button aria-label="适应画布" title="适应画布" onClick={fit}>
                        <Scan size={17} />
                    </button>
                    {document.fullscreenEnabled && (
                        <button aria-label={fullscreen ? '退出全屏' : '全屏查看导图'} title={fullscreen ? '退出全屏' : '全屏'} onClick={toggleFullscreen}>
                            {fullscreen ? <Minimize size={17} /> : <Maximize size={17} />}
                        </button>
                    )}
                </div>
            </div>
            <svg
                ref={svgRef}
                className="inventory-map-canvas"
                role="group"
                aria-label={`${tree.text}思维导图`}
                onPointerDown={pointerDown}
                onPointerMove={pointerMove}
                onPointerUp={pointerEnd}
                onPointerCancel={pointerEnd}
            >
                <g transform={`translate(${view.x},${view.y}) scale(${view.scale})`} visibility={viewport.width ? 'visible' : 'hidden'}>
                    <g className="inventory-map-branches" fill="none">
                        {layout.links.map((link) => (
                            <path
                                key={link.to.id}
                                d={branchPath(link)}
                                stroke={link.to.theme[0]}
                                strokeWidth={link.to.depth === 1 ? 2.2 : 1.4}
                                opacity={link.to.depth === 1 ? 0.65 : 0.4}
                            />
                        ))}
                    </g>
                    {layout.nodes.map((node) => {
                        const canToggle = node.childCount > 0
                        const closed = folded.has(node.id)
                        const textY = (-(node.lines.length - 1) * node.lineHeight) / 2
                        return (
                            <g
                                key={node.id}
                                className={`inventory-map-node inventory-map-node--${node.depth === 0 ? 'root' : node.depth === 1 ? 'topic' : 'detail'}`}
                                transform={`translate(${node.x},${node.y})`}
                                style={{ '--branch-color': node.theme[0], '--branch-tint': node.theme[1] }}
                                data-map-node={node.id}
                                data-map-toggle={canToggle ? '' : undefined}
                                role={canToggle ? 'button' : undefined}
                                tabIndex={canToggle ? 0 : undefined}
                                aria-label={canToggle ? `${closed ? '展开' : '收起'}：${node.text}` : undefined}
                                aria-expanded={canToggle ? !closed : undefined}
                                onClick={canToggle ? () => toggle(node) : undefined}
                                onKeyDown={
                                    canToggle
                                        ? (event) => {
                                              if (event.key === 'Enter' || event.key === ' ') {
                                                  event.preventDefault()
                                                  toggle(node)
                                              }
                                          }
                                        : undefined
                                }
                            >
                                <title>{node.text}</title>
                                <rect
                                    className="inventory-map-node-box"
                                    width={node.width}
                                    height={node.height}
                                    y={-node.height / 2}
                                    rx={node.depth === 0 ? 12 : 8}
                                />
                                <text x={node.depth < 2 ? node.width / 2 : 16} textAnchor={node.depth < 2 ? 'middle' : 'start'} dominantBaseline="central">
                                    {node.lines.map((line, index) => (
                                        <tspan key={index} x={node.depth < 2 ? node.width / 2 : 16} y={textY + index * node.lineHeight}>
                                            {line}
                                        </tspan>
                                    ))}
                                </text>
                                {canToggle && (
                                    <g className="inventory-map-toggle" transform={`translate(${node.side === -1 ? 0 : node.width},0)`}>
                                        <circle r={9} />
                                        {closed ? (
                                            <text textAnchor="middle" dominantBaseline="central">
                                                {node.childCount}
                                            </text>
                                        ) : (
                                            <Minus x={-5} y={-5} width={10} height={10} strokeWidth={1.8} />
                                        )}
                                    </g>
                                )}
                            </g>
                        )
                    })}
                </g>
            </svg>
            <div className="inventory-map-caption">
                <span>拖动平移 · 滚轮缩放 · 点击节点展开</span>
                <span>颜色区分主题，数字表示下级节点数</span>
            </div>
        </div>
    )
}

export default function ReportMindmap({ markdown, reportType = 'inventory' }) {
    const tree = useMemo(() => {
        try {
            return parseMindmap(markdown, reportType)
        } catch (error) {
            console.error('报告导图渲染失败', error)
            return null
        }
    }, [markdown, reportType])
    return tree ? (
        <MindmapCanvas key={markdown} tree={tree} />
    ) : (
        <div className="inventory-state" role="alert">
            导图暂时无法展示，请切换到“报告正文”阅读。
        </div>
    )
}
